import {
  getInitialData,
  scwFetch,
  clearDynamicContainer,
  setContainerHeader,
  handleError,
  ScwError,
  clearContainerElement,
  dynamicContainer,
} from "../main";
import { broadcastSsoStateChange } from "../utils/broadcast";
import { showToast } from "../utils/toast-message";
import { onSubmit } from "../utils/form";
import { initPasswordToggles } from "../utils/password-toggle";

interface IdpDisplayInfo {
  id: string;
  displayName: string;
  iconUrl: string;
}

interface LoginState {
  state: string;
  justIdp: boolean;
  idpListCommon: IdpDisplayInfo[];
  idpListCustom: IdpDisplayInfo[];
  loginString: string;
  continueSelfIdpLogin: boolean;
  environmentReadableName: string;
  loginChallenge: string;
}

interface LoginResponse {
  redirectUrl?: string;
  redirectUrlAfterCallback?: string;
  hint?: "change_pass_req" | string;
}

interface IdpRequiredResponse {
  justLoginIdp?: boolean;
}

interface IdpCheckResponse {
  isValid?: boolean;
}

const state = getInitialData<LoginState>();

const ssoChallenge = localStorage.getItem("state");
if (ssoChallenge !== state.state) {
  localStorage.clear();
  localStorage.setItem("state", state.state);
  broadcastSsoStateChange({ action: "SSO_NEW_SESSION" });
  localStorage.setItem("login_challenge", state.loginChallenge);
}

const templates = {
  loginMethodSelection: document.getElementById(
    "template_loginMethodSelection",
  ) as HTMLTemplateElement,
  passwordForm: document.getElementById(
    "template_passwordForm",
  ) as HTMLTemplateElement,
  passwordFormPostCheck: document.getElementById(
    "template_passwordFormPostCheck",
  ) as HTMLTemplateElement,
  customIdpForm: document.getElementById(
    "template_customIdpForm",
  ) as HTMLTemplateElement,
  idpButton: document.getElementById(
    "template_idpButton",
  ) as HTMLTemplateElement,
};

const createIdpButton = (
  idpType: string,
  idpInfo: IdpDisplayInfo,
  idpButtonContainerFormElement: HTMLFormElement,
) => {
  let idpButtonElement = (
    templates.idpButton?.content.cloneNode(true) as DocumentFragment
  ).firstElementChild as HTMLButtonElement;
  let buttonTextSpan = idpButtonElement.querySelector(
    "span",
  ) as HTMLSpanElement;
  let buttonIcon = idpButtonElement.querySelector(
    "img.scw-idp-btn-logo",
  ) as HTMLImageElement;

  buttonIcon.src = idpInfo.iconUrl;
  buttonTextSpan.textContent = idpInfo.displayName;
  idpButtonElement.value = idpInfo.id;
  idpButtonElement.dataset.idpType = idpType;
  idpButtonElement.append(buttonTextSpan);
  idpButtonContainerFormElement.appendChild(idpButtonElement);
};

const switchToLoginMethodSelection = () => {
  clearDynamicContainer();

  setContainerHeader({
    title: "Login",
    subTitle: "Select your preferred login method",
  });

  const elementFragment = templates.loginMethodSelection?.content.cloneNode(
    true,
  ) as DocumentFragment;
  const idpButtonContainerFormElement = elementFragment.querySelector(
    "form",
  ) as HTMLFormElement;

  if (!state.justIdp) {
    const selfIdp: IdpDisplayInfo = {
      iconUrl: "/assets/images/user.svg",
      displayName: "Username or Email",
      id: "password",
    };

    createIdpButton("self", selfIdp, idpButtonContainerFormElement);
  }

  for (let commonIdp of state.idpListCommon) {
    createIdpButton("common", commonIdp, idpButtonContainerFormElement);
  }

  for (let customIdp of state.idpListCustom) {
    createIdpButton("custom", customIdp, idpButtonContainerFormElement);
  }

  idpButtonContainerFormElement.addEventListener("submit", async (event) => {
    event.preventDefault();
    const submitter = event.submitter as HTMLButtonElement;

    switch (submitter.dataset.idpType) {
      case "self":
        switchToLoginForm();
        break;
      case "common":
        window.location.replace(
          "/view/external-idp/" + state.state + "/common/" + submitter.value,
        );
        break;
      case "custom":
        if (state.loginString) {
          window.location.replace(
            "/view/external-idp/" +
              state.state +
              "/custom/" +
              state.loginString +
              "/" +
              submitter.value,
          );
          break;
        }
        switchToCustomIdpForm(submitter.value);
        break;
    }
  });

  dynamicContainer.appendChild(elementFragment);
};

const selfIdpLogin = async (
  formData: FormData,
  notificationContainer?: HTMLDivElement | null,
) => {
  clearContainerElement(notificationContainer);

  const loginPayload: any = Object.fromEntries(formData);
  loginPayload["login_string"] ??= state.loginString;
  loginPayload.challenge =
    state.loginChallenge ?? localStorage.getItem("login_challenge");

  let loginChallengeURL = localStorage.getItem("login");

  if (!loginChallengeURL) {
    const loginReq = await scwFetch<LoginResponse>(
      "/api/auth/login/" + state.state,
      {
        method: "POST",
        body: loginPayload,
        notificationContainer,
      },
    );

    loginChallengeURL = loginReq.redirectUrl ?? "";
    localStorage.setItem("login", loginChallengeURL as string);

    if (loginReq.hint === "change_pass_req") {
      const changePassUrl = new URL(
        "/view/change-password/" + state.state,
        window.location.origin,
      );
      changePassUrl.searchParams.set("login_challenge", loginPayload.challenge);

      window.location.replace(changePassUrl);
      return;
    }

    if (loginReq.redirectUrl) {
      broadcastSsoStateChange({
        action: "SSO_LOGIN",
        payload: { url: loginReq.redirectUrlAfterCallback },
      });
      window.location.replace(loginReq.redirectUrl);
      return;
    }
  }
};

const switchToLoginForm = () => {
  clearDynamicContainer();
  setContainerHeader({
    title: "Login",
    subTitle: "Please enter your details.",
    backButtonAction: switchToLoginMethodSelection,
  });

  const containerFragment = templates.passwordForm?.content.cloneNode(
    true,
  ) as DocumentFragment;
  const form = containerFragment.firstElementChild?.querySelector(
    "form",
  ) as HTMLFormElement;
  const submitButton = containerFragment.querySelector(
    "button[form=passwordForm]",
  ) as HTMLButtonElement;
  const notificationContainer = containerFragment.querySelector(
    "div.scw-toast-container[data-toast-notification-container]",
  ) as HTMLDivElement;

  if (state.loginString) {
    const loginStringInput = form.querySelector(
      "input#login_string",
    ) as HTMLInputElement;

    if (loginStringInput) {
      loginStringInput.value = state.loginString;
      loginStringInput.disabled = true;
    }
  }

  onSubmit(
    form,
    async (formData) => {
      clearContainerElement(notificationContainer);

      if (!formData.has("password")) {
        const isIdpRequiredReq = await scwFetch<IdpRequiredResponse>(
          "/api/auth/is-idp-required/" + state.state,
          {
            method: "POST",
            body: {
              login_string: formData.get("login_string") ?? state.loginString,
              username: formData.get("username"),
            },
            notificationContainer,
          },
        );

        if (isIdpRequiredReq?.justLoginIdp) {
          handleError(
            new ScwError(
              'Please use "SSO Login" for this user. Please contact your system owner or SCW.AI for details.',
            ),
            notificationContainer,
          );
          return;
        }

        if (!dynamicContainer.querySelector("#password")) {
          const passwordFragment =
            templates.passwordFormPostCheck.content.cloneNode(
              true,
            ) as DocumentFragment;
          initPasswordToggles(passwordFragment);
          (
            dynamicContainer.querySelector(
              "form#passwordForm",
            ) as HTMLFormElement
          ).append(passwordFragment);
          (
            dynamicContainer.querySelector(
              "button[form=passwordForm]",
            ) as HTMLButtonElement
          ).textContent =
            "Login" +
            (state.environmentReadableName
              ? ` (${state.environmentReadableName})`
              : "");
        }
        return;
      }

      await selfIdpLogin(formData, notificationContainer);
    },
    submitButton,
  );

  dynamicContainer.appendChild(containerFragment);
};

const switchToCustomIdpForm = (idp: string) => {
  clearDynamicContainer();
  setContainerHeader({
    title: "Custom Login",
    subTitle: "",
    backButtonAction: () => switchToLoginMethodSelection(),
  });
  const customIdpFragment = templates.customIdpForm?.content.cloneNode(
    true,
  ) as DocumentFragment;
  let customIdpForm = customIdpFragment.querySelector(
    "form#customIdpForm",
  ) as HTMLFormElement;
  const notificationContainer = customIdpFragment.querySelector(
    "div.scw-toast-container[data-toast-notification-container]",
  ) as HTMLDivElement;

  const submitButton = customIdpFragment.querySelector(
    "button[form=customIdpForm]",
  ) as HTMLButtonElement;

  onSubmit(
    customIdpForm,
    async (formData) => {
      clearContainerElement(notificationContainer);

      let checkResponse = await scwFetch<IdpCheckResponse>(
        "/api/external-idp/" +
          state.state +
          "/custom/check/" +
          idp +
          "/" +
          formData.get("loginString"),
        { method: "GET" },
      );

      if (checkResponse.isValid) {
        window.location.replace(
          "/view/external-idp/" +
            state.state +
            "/custom/" +
            formData.get("loginString") +
            "/" +
            idp,
        );
        return;
      }

      const idpConfig =
        state.idpListCommon?.find((cidp) => cidp.id === idp) ??
        state.idpListCustom?.find((cidp) => cidp.id === idp);

      handleError(
        new ScwError(
          `${idpConfig?.displayName ?? idp} integration has not been setup for this client. Please contact your system owner or SCW.AI for details.`,
        ),
        notificationContainer,
      );
    },
    submitButton,
  );

  dynamicContainer.appendChild(customIdpFragment);
};

if (state.continueSelfIdpLogin) {
  let loginChallengeURL = localStorage.getItem("login");
  if (loginChallengeURL) {
    window.location.replace(loginChallengeURL);
  }
}

switchToLoginMethodSelection();
